import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { superAdminAPI, adminSettingsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Shield, Users, Building2, Store, Tag, Loader2, ClipboardList, Gift, Activity } from 'lucide-react';

import { AccountsTab } from '../components/superadmin/AccountsTab';
import { CouponsTab } from '../components/superadmin/CouponsTab';
import { CreateAccountDialog } from '../components/superadmin/CreateAccountDialog';
import { CreateCouponDialog } from '../components/superadmin/CreateCouponDialog';
import { EditAccountDialog } from '../components/superadmin/EditAccountDialog';
import { DeleteAccountDialog } from '../components/superadmin/DeleteAccountDialog';
import { EditCouponDialog } from '../components/superadmin/EditCouponDialog';
import { DeleteCouponDialog } from '../components/superadmin/DeleteCouponDialog';
import LifetimeTab from '../components/superadmin/LifetimeTab';
import MCPTab from '../components/superadmin/MCPTab';

export default function SuperAdmin() {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('accounts');
  const [submitting, setSubmitting] = useState(false);

  // Account dialogs
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountForm, setAccountForm] = useState({
    email: '', password: '', name: '', brand_name: '', plan_type: 'single',
  });
  const [editAccountDialogOpen, setEditAccountDialogOpen] = useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(null);
  const [editAccountForm, setEditAccountForm] = useState({ brand_name: '', owner_name: '', owner_email: '', owner_role: 'owner' });

  // Coupon dialogs
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '', discount_percent: 10, max_uses: '', expires_at: '',
    applicable_plans: ['single', 'multi'], applicable_cycles: ['monthly', 'yearly'],
  });
  const [editCouponDialogOpen, setEditCouponDialogOpen] = useState(false);
  const [deleteCouponDialogOpen, setDeleteCouponDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [deletingCoupon, setDeletingCoupon] = useState(null);
  const [editCouponForm, setEditCouponForm] = useState({
    discount_percent: '', max_uses: '', expires_at: '',
    applicable_plans: ['single', 'multi'], applicable_cycles: ['monthly', 'yearly'],
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [accountsRes, couponsRes] = await Promise.all([
        superAdminAPI.getAccounts(),
        superAdminAPI.getCoupons(),
      ]);
      setAccounts(accountsRes.data);
      setCoupons(couponsRes.data);
      try {
        const auditRes = await adminSettingsAPI.getStripeAuditLog();
        setAuditLogs(auditRes.data);
      } catch (_) { /* non-critical */ }
    } catch (err) {
      console.error('Error fetching super admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  // ── Account handlers ────────────────────────────────────────────────────────

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await superAdminAPI.createAccount(accountForm);
      toast.success('Account created!');
      setAccountDialogOpen(false);
      setAccountForm({ email: '', password: '', name: '', brand_name: '', plan_type: 'single' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (groupId, newStatus) => {
    try {
      await superAdminAPI.updateStatus(groupId, newStatus);
      toast.success('Status updated');
      fetchData();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      const res = await superAdminAPI.impersonate(userId);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      toast.success('Impersonating user');
      window.location.href = '/dashboard';
    } catch {
      toast.error('Failed to impersonate user');
    }
  };

  const openEditAccount = (account) => {
    setEditingAccount(account);
    setEditAccountForm({
      brand_name: account.brand_name || '',
      owner_name: account.owner_name || '',
      owner_email: account.owner_email || '',
      owner_role: account.owner_role || 'owner',
    });
    setEditAccountDialogOpen(true);
  };

  const handleEditAccount = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Update basic account info
      await superAdminAPI.editAccount(editingAccount.id, {
        brand_name: editAccountForm.brand_name || undefined,
        owner_name: editAccountForm.owner_name || undefined,
        owner_email: editAccountForm.owner_email || undefined,
      });
      
      // Update owner role if changed
      if (editAccountForm.owner_role && editAccountForm.owner_role !== editingAccount.owner_role) {
        await superAdminAPI.updateOwnerRole(editingAccount.id, editAccountForm.owner_role);
      }
      
      toast.success('Account updated');
      setEditAccountDialogOpen(false);
      setEditingAccount(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletingAccount) return;
    setSubmitting(true);
    try {
      await superAdminAPI.deleteAccount(deletingAccount.id);
      toast.success('Account deleted');
      setDeleteAccountDialogOpen(false);
      setDeletingAccount(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRunBackfill = async () => {
    if (!window.confirm('Run tenant defaults backfill? This will ensure all accounts have business settings and AI configuration.')) {
      return;
    }
    setSubmitting(true);
    try {
      const res = await superAdminAPI.backfillTenantDefaults();
      const result = res.data;
      toast.success(
        `Backfill complete: ${result.updated || 0} updated, ${result.already_complete || 0} already complete, ${result.failed || 0} failed`,
        { duration: 5000 }
      );
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run backfill');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Coupon handlers ─────────────────────────────────────────────────────────

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    if (couponForm.applicable_plans.length === 0) { toast.error('Select at least one plan'); return; }
    if (couponForm.applicable_cycles.length === 0) { toast.error('Select at least one billing cycle'); return; }
    setSubmitting(true);
    try {
      await superAdminAPI.createCoupon({
        ...couponForm,
        max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
        expires_at: couponForm.expires_at || null,
      });
      toast.success('Coupon created!');
      setCouponDialogOpen(false);
      setCouponForm({ code: '', discount_percent: 10, max_uses: '', expires_at: '', applicable_plans: ['single', 'multi'], applicable_cycles: ['monthly', 'yearly'] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create coupon');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      await superAdminAPI.toggleCoupon(couponId);
      toast.success('Coupon toggled');
      fetchData();
    } catch {
      toast.error('Failed to toggle coupon');
    }
  };

  const openEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setEditCouponForm({
      discount_percent: String(coupon.discount_percent),
      max_uses: coupon.max_uses ? String(coupon.max_uses) : '',
      expires_at: coupon.expires_at || '',
      applicable_plans: coupon.applicable_plans || ['single', 'multi'],
      applicable_cycles: coupon.applicable_cycles || ['monthly', 'yearly'],
    });
    setEditCouponDialogOpen(true);
  };

  const handleEditCoupon = async (e) => {
    e.preventDefault();
    if (editCouponForm.applicable_plans.length === 0) { toast.error('Select at least one plan'); return; }
    if (editCouponForm.applicable_cycles.length === 0) { toast.error('Select at least one billing cycle'); return; }
    setSubmitting(true);
    try {
      await superAdminAPI.editCoupon(editingCoupon.id, {
        discount_percent: parseInt(editCouponForm.discount_percent),
        max_uses: editCouponForm.max_uses ? parseInt(editCouponForm.max_uses) : 0,
        expires_at: editCouponForm.expires_at || null,
        applicable_plans: editCouponForm.applicable_plans,
        applicable_cycles: editCouponForm.applicable_cycles,
      });
      toast.success('Coupon updated');
      setEditCouponDialogOpen(false);
      setEditingCoupon(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update coupon');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCoupon = async () => {
    if (!deletingCoupon) return;
    setSubmitting(true);
    try {
      const res = await superAdminAPI.deleteCoupon(deletingCoupon.id);
      toast.success(res.data.message || 'Coupon removed');
      setDeleteCouponDialogOpen(false);
      setDeletingCoupon(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete coupon');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Shield className="text-red-600" size={24} />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Super Admin
            </h1>
            <p className="text-slate-500">Platform management</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{accounts.length}</p>
                  <p className="text-sm text-slate-500">Accounts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Store className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {accounts.reduce((sum, a) => sum + (a.restaurant_count || 0), 0)}
                  </p>
                  <p className="text-sm text-slate-500">Restaurants</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="text-purple-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {accounts.reduce((sum, a) => sum + (a.staff_count || 0), 0)}
                  </p>
                  <p className="text-sm text-slate-500">Total Staff</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Tag className="text-orange-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{coupons.filter(c => c.active).length}</p>
                  <p className="text-sm text-slate-500">Active Coupons</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="-mx-1 overflow-x-auto sm:overflow-visible">
            <TabsList className="w-max sm:w-auto">
              <TabsTrigger value="accounts" data-testid="tab-accounts">Accounts</TabsTrigger>
              <TabsTrigger value="coupons" data-testid="tab-coupons">Coupons</TabsTrigger>
              <TabsTrigger value="audit-log" data-testid="tab-audit-log">
                <ClipboardList size={14} className="mr-1.5" />
                Audit Log
              </TabsTrigger>
              <TabsTrigger value="lifetime" data-testid="tab-lifetime">
                <Gift size={14} className="mr-1.5" />
                Lifetime
              </TabsTrigger>
              <TabsTrigger value="mcp" data-testid="tab-mcp">
                <Activity size={14} className="mr-1.5" />
                MCP
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="accounts">
            <AccountsTab
              accounts={accounts}
              onCreateAccount={() => setAccountDialogOpen(true)}
              onImpersonate={handleImpersonate}
              onEdit={openEditAccount}
              onUpdateStatus={handleUpdateStatus}
              onDelete={(account) => { setDeletingAccount(account); setDeleteAccountDialogOpen(true); }}
              onRunBackfill={handleRunBackfill}
            />
          </TabsContent>

          <TabsContent value="coupons">
            <CouponsTab
              coupons={coupons}
              onCreateCoupon={() => setCouponDialogOpen(true)}
              onToggle={handleToggleCoupon}
              onEdit={openEditCoupon}
              onDelete={(coupon) => { setDeletingCoupon(coupon); setDeleteCouponDialogOpen(true); }}
            />
          </TabsContent>

          <TabsContent value="audit-log">
            <Card>
              <CardContent className="p-0">
                {auditLogs.length === 0 ? (
                  <div className="py-16 text-center text-slate-400 text-sm">No Stripe configuration changes recorded yet.</div>
                ) : (
                  <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-sm" data-testid="audit-log-table">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50 text-left">
                        <th className="px-5 py-3 font-medium text-slate-500">Date</th>
                        <th className="px-5 py-3 font-medium text-slate-500">User</th>
                        <th className="px-5 py-3 font-medium text-slate-500">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log) => (
                        <tr key={log.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                          <td className="px-5 py-3 text-slate-600 whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleString()}
                          </td>
                          <td className="px-5 py-3 text-slate-700">{log.performed_by_email || log.performed_by}</td>
                          <td className="px-5 py-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-100">
                              {log.action}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="lifetime">
            <LifetimeTab />
          </TabsContent>

          <TabsContent value="mcp">
            <MCPTab />
          </TabsContent>
        </Tabs>
        <CreateAccountDialog
          open={accountDialogOpen}
          onClose={() => setAccountDialogOpen(false)}
          onSubmit={handleCreateAccount}
          accountForm={accountForm}
          setAccountForm={setAccountForm}
          submitting={submitting}
        />

        <CreateCouponDialog
          open={couponDialogOpen}
          onClose={() => setCouponDialogOpen(false)}
          onSubmit={handleCreateCoupon}
          couponForm={couponForm}
          setCouponForm={setCouponForm}
          submitting={submitting}
        />

        <EditAccountDialog
          open={editAccountDialogOpen}
          onClose={() => setEditAccountDialogOpen(false)}
          onSubmit={handleEditAccount}
          editAccountForm={editAccountForm}
          setEditAccountForm={setEditAccountForm}
          submitting={submitting}
        />

        <DeleteAccountDialog
          open={deleteAccountDialogOpen}
          onClose={() => setDeleteAccountDialogOpen(false)}
          onDelete={handleDeleteAccount}
          deletingAccount={deletingAccount}
          submitting={submitting}
        />

        <EditCouponDialog
          open={editCouponDialogOpen}
          onClose={() => setEditCouponDialogOpen(false)}
          onSubmit={handleEditCoupon}
          editingCoupon={editingCoupon}
          editCouponForm={editCouponForm}
          setEditCouponForm={setEditCouponForm}
          submitting={submitting}
        />

        <DeleteCouponDialog
          open={deleteCouponDialogOpen}
          onClose={() => setDeleteCouponDialogOpen(false)}
          onDelete={handleDeleteCoupon}
          deletingCoupon={deletingCoupon}
          submitting={submitting}
        />

      </div>
    </Layout>
  );
}
